import ServicePageHero from "../components/ServicePageHero";
import { openConsultationMail } from "../config/contact";
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiArrowUpRight, FiPlus, FiChevronDown, FiChevronUp, FiShoppingCart, FiTrendingUp, FiShield, FiSettings } from "react-icons/fi";
import { Helmet } from "react-helmet-async";
import ServiceDetailSection from "../components/ServiceDetailSection";
import ServiceIntroSection from "../components/ServiceIntroSection";

// Using specific assets for Ecommerce
import img_charts_pana from "../assets/Charts-pana.png";
import img_consultative_sales_amico from "../assets/Consultative sales-amico.png";
import img_conversion_rate_optimization_pana_1 from "../assets/Conversion rate optimization-pana (1).png";
import img_conversion_rate_optimization_rafiki from "../assets/Conversion rate optimization-rafiki.png";
import img_generating_new_leads_pana_1 from "../assets/Generating new leads-pana (1).png";
import img_product_presentation_amico from "../assets/Product presentation-amico.png";
import img_about_img1 from "../assets/Choose-bro.png";
import img_about_img2 from "../assets/Starting a business project-amico.png";

const FAQ_DATA = [
  {
    question: "1. How long does it take to develop an ecommerce website?",
    answer: "The timeline depends on the complexity of the website. A basic ecommerce website can take 2–4 weeks, while more complex projects with custom features may take longer."
  },
  {
    question: "2. Will my ecommerce website be mobile-friendly?",
    answer: "Yes, we create responsive ecommerce websites that work smoothly across all devices, ensuring a great shopping experience on mobile and tablets."
  },
  {
    question: "3. Can I manage products and inventory easily?",
    answer: "Yes, we build ecommerce platforms with user-friendly dashboards that allow you to easily add products, manage inventory, and process orders without technical knowledge."
  },
  {
    question: "4. Do you provide secure payment gateway integration?",
    answer: "Yes, we integrate secure and reliable payment gateways to ensure safe transactions for your customers."
  }
];

const scrollReveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: "easeOut" },
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemFade = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};


const WHY_ICONS = [
  <FiShoppingCart />,
  <FiTrendingUp />,
  <FiShield />,
  <FiSettings />,
];
const EcommerceServicesPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeWhy, setActiveWhy] = useState(0);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const scrollToContact = openConsultationMail; // replaced

  return (
    <>
      <Helmet>
        <title>Ecommerce Services Company | Ecommerce Marketing Experts</title>
        <meta name="description" content="Grow your online store with ecommerce marketing services. Hire experts for SEO, ads, and performance marketing for ecommerce businesses." />
      </Helmet>
      <main className="ecommerce-services-page">
      <ServicePageHero
        badge="Our Services"
        title="Best Ecommerce Services"
        subtitle="Build a powerful, secure, and scalable online store to grow your sales."
        breadcrumb="Home > Services > Ecommerce"
        image={img_consultative_sales_amico}
      />


      {/* ABOUT SECTION */}
      <ServiceIntroSection
        heading="How an Ecommerce Store Drives Sales Growth"
        imageLeft={img_generating_new_leads_pana_1}
        imageRight={img_conversion_rate_optimization_rafiki}
        paragraph1="A robust ecommerce website allows you to sell your products 24/7, reach a wider audience, and provide a seamless shopping experience for your customers."
        paragraph2="As a professional ecommerce development company, we focus on creating secure, user-friendly, and high-converting online stores."
      />

      <ServiceDetailSection
        isReversed={false}
        label="Our Ecommerce Strategy"
        title="Platform Selection & Planning"
        description="We start by understanding your product range, target audience, and business goals to choose the right ecommerce platform (like Shopify, WooCommerce, or custom solutions)."
        longDescription="A well-planned architecture ensures your store is scalable and easy to manage as your business grows."
        image={img_product_presentation_amico}
      />

      <ServiceDetailSection
        isReversed={true} bgLight
        title="Store Design & User Experience"
        description="We create visually appealing and intuitive store designs that highlight your products and make navigation effortless for shoppers."
        longDescription="A seamless user experience reduces cart abandonment and encourages customers to complete their purchases."
        image={img_conversion_rate_optimization_pana_1}
      />

      <ServiceDetailSection
        isReversed={false}
        title="Development & Integration"
        description="We build fast, secure, and mobile-responsive online stores. Our development includes integrating essential features like secure payment gateways, inventory management, and shipping calculators."
        longDescription="This ensures a smooth and reliable shopping experience for your customers from browsing to checkout."
        image={img_charts_pana}
      />

      <ServiceDetailSection
        isReversed={true} bgLight
        title="Testing & Launch"
        description="Before going live, we rigorously test your ecommerce store for performance, security, and usability across all devices and browsers."
        longDescription="We ensure every feature works perfectly so your store is ready to generate sales from day one."
        image={img_generating_new_leads_pana_1}
      />

      {/* WHY CHOOSE US - DARK VERSION */}
      <section className="dark-why-section">
        <div className="container-custom">
          <motion.div className="dark-why-header" {...scrollReveal}>
            <span className="dark-why-badge">Why Choose Us</span>
            <h2>Why Businesses Trust Our Ecommerce Expertise</h2>
          </motion.div>

          <motion.div 
            className="dark-why-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, amount: 0.1 }}
          >
            <motion.div className="dark-why-list" variants={itemFade}>
              {[
                { 
                  title: "1. Custom Ecommerce Solutions", 
                  desc: "We create online stores tailored to your specific business needs and product offerings. This ensures your store stands out and provides a unique shopping experience. Our customized approach helps maximize conversions and sales." 
                },
                { 
                  title: "2. Conversion-Focused Design", 
                  desc: "We focus on creating store layouts that guide visitors smoothly towards making a purchase. Clear product displays and easy checkout processes help increase your sales. A good user experience is key to ecommerce success." 
                },
                { 
                  title: "3. Secure & Reliable Platforms", 
                  desc: "We develop stores with robust security measures to protect customer data and transactions. This builds trust and credibility for your brand. A secure platform is essential for long-term ecommerce growth." 
                },
                { 
                  title: "4. Ongoing Support & Maintenance", 
                  desc: "We provide continuous support to keep your store updated, secure, and performing optimally. This helps you focus on your business while we handle the technical side. Regular updates ensure your store stays competitive." 
                }
              ].map((item, idx) => (
                <div 
                  key={idx} 
                  className={`dark-why-item ${activeWhy === idx ? 'active' : ''}`}
                  onMouseEnter={() => setActiveWhy(idx)}
                >
                  <div className="dark-why-item-header">
                    <span className="dark-why-item-icon-left">{WHY_ICONS[idx]}</span>
                    <h4>{item.title}</h4>
                    <span className="dark-why-item-icon">
                      {activeWhy === idx ? <FiArrowUpRight /> : <FiPlus />}
                    </span>
                  </div>
                  <div className="dark-why-item-content">
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div className="dark-why-visual" variants={itemFade}>
              <img src={img_about_img1} alt="Expertise 1" className="dark-oval-1" />
              <img src={img_about_img2} alt="Expertise 2" className="dark-oval-2" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="faq-section py-20">
        <div className="container-custom max-w-4xl">
          <motion.h2 className="section-title text-center mb-16" {...scrollReveal}>Frequently Asked Questions</motion.h2>
          <div className="faq-list">
            {FAQ_DATA.map((faq, index) => (
              <motion.div 
                key={index} 
                className={`faq-item ${openFaq === index ? 'active' : ''}`}
                {...scrollReveal}
                transition={{ delay: index * 0.1 }}
              >
                <button className="faq-question" onClick={() => toggleFaq(index)}>
                  <span>{faq.question}</span>
                  {openFaq === index ? <FiChevronUp /> : <FiChevronDown />}
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div 
                      className="faq-answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <p>{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="service-page-cta service-page-cta--soft py-20">
        <div className="container-custom">
          <motion.div 
            className="service-page-cta-inner text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Start Selling Online Successfully</h2>
            <p className="mb-8">Our ecommerce development services in Delhi NCR are focused on creating secure, high-performing online stores that drive sales and support your business growth.</p>
            <button className="nav-btn mx-auto" onClick={scrollToContact} style={{ padding: '14px 32px', fontSize: '16px' }}>
              Get Free Consultation
              <span className="nav-btn-arrow" style={{ width: '28px', height: '28px' }}>
                <FiArrowUpRight />
              </span>
            </button>
          </motion.div>
        </div>
      </section>
      </main>
    </>
  );
};

export default EcommerceServicesPage;
